import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LangService } from 'src/app/core/lang.service';
import { StyleprofileService } from 'src/app/core/styleprofile.service';
import { EditingDisabledService } from 'src/app/ui-item-maker/editing-disabled.service';

@Component({
  selector: 'capture-math',
  templateUrl: './capture-math.component.html',
  styleUrls: ['./capture-math.component.scss']
})
export class CaptureMathComponent implements OnInit, OnDestroy, OnChanges {

  @Input() obj:any;
  @Input() prop:string;
  @Input() startBlank:boolean;
  @Input() isManualKeyboard:boolean;
  @Input() isSpacesTabs:boolean;
  @Input() isLocked:boolean;
  @Input() latexValue:string;
  @Input() control: FormControl;
  @Input() ariaLabel:string;
  @Input() screenReaderText:string;
  @Input() screenReaderTextId:string;
  @Output() onChange = new EventEmitter<null>();
  @ViewChild('mathField', { static: true }) mathFieldRef: ElementRef;

  MathLive = null;
  mathField;
  isMathFieldLocked:boolean = false;
  private keyboardToggle: HTMLElement;
  private keyboardA11yTimer: number;
  private mathTextInput: HTMLElement;
  private mathInputLabelObserver: MutationObserver;

  constructor(private editingDisabled: EditingDisabledService,
    private profile: StyleprofileService, 
    private lang: LangService) { }

  ngOnInit() {
    this.clearPreviousKeyboards();
  }

  ngOnDestroy(){
    if (this.keyboardA11yTimer) {
      window.clearTimeout(this.keyboardA11yTimer);
    }
    if (this.keyboardToggle) {
      this.keyboardToggle.removeEventListener('click', this.scheduleKeyboardAccessibilityUpdate);
      this.keyboardToggle.removeEventListener('keyup', this.scheduleKeyboardAccessibilityUpdate);
    }
    if (this.mathInputLabelObserver) {
      this.mathInputLabelObserver.disconnect();
    }
  }

  clearPreviousKeyboards(){
    const lingeringKeyboards = document.getElementsByClassName('ML__keyboard');
    if (lingeringKeyboards){
      // console.log('on destroy :: ', lingeringKeyboards.length);
      for (let i=0; i<lingeringKeyboards.length; i++){
        const el = lingeringKeyboards[i];  
        el.parentElement.removeChild(el)
      }
    }
  }

  ngOnChanges(){
    if(this.isLocked && !this.isMathFieldLocked){
      this.isMathFieldLocked = true;
      let currentLatex = this.latexValue !== undefined ? this.latexValue : this.obj[this.prop];
      let staticMathFieldContainer = this.mathFieldRef.nativeElement;
      if (!currentLatex) currentLatex='';
      staticMathFieldContainer.innerHTML = '$$'+currentLatex+'$$';
      this.MathLive.renderMathInElement(staticMathFieldContainer);
    }
    this.updateScreenReaderAttributes();
  }

  ngAfterViewInit() {
    const language = this.lang.c();
    const styleProfile = this.profile.getStyleProfile()[language].configuration.math;
    // console.log('mathFieldRef', this.mathFieldRef.nativeElement)
    this.MathLive = (window as any).MathLive;
    if (this.isMathFieldLocked) return;
    const mathField =  this.MathLive.makeMathField(this.mathFieldRef.nativeElement, {
      locale: language,
      smartMode: false,
      smartFence: styleProfile.smartFence,
      ignoreSpacebarInMathMode: false, 
      virtualKeyboards: 'numeric',
      virtualKeyboardMode: this.isManualKeyboard ? 'manual' : 'onfocus',
      onKeystroke: (mf:any, keystroke:string, ev:{key:string, type:string}) => {
        if (ev.type === 'keydown'){
          // If we do not force these in, they do not appear.
          switch (ev.key){
            case '%':
            case '$':
              mf.$insert('\\'+ev.key);
              // mf.$insert(ev.key);
          }
        }
        return true;
      },
      onContentDidChange: mf => {
        let newLatex:string = mathField.$latex();
        let newLatexClean = newLatex.replace('#@', '\\placeholder{}');
        newLatexClean = newLatexClean.replace('\\imaginaryI', 'i')
        newLatexClean = newLatexClean.replace('\\exponentialE', 'e')

        if (newLatex !== newLatexClean){
          newLatex = newLatexClean;
          mathField.$latex(newLatexClean);
        }
        if (this.obj){
          if (this.prop && newLatex !== this.obj[this.prop]){
            this.obj[this.prop] = newLatex;
            if (!this.obj._changeCounter){ this.obj._changeCounter = 1; }
            else{ this.obj._changeCounter ++; }
          }
          if (this.latexValue !== undefined) {
            this.control.setValue(newLatex);
            this.latexValue = newLatex;
          }
          this.onChange.emit();
        }
        this.scheduleKeyboardAccessibilityUpdate();
      }
    });
    if (this.prop && this.obj[this.prop]){
      this.obj[this.prop] = mathField.$latex(this.obj[this.prop])
    }
    if (this.latexValue !== undefined) {
      this.latexValue = mathField.$latex(this.latexValue)
    }
    this.updateScreenReaderAttributes();
  }

  updateScreenReaderAttributes() {
    if (!this.mathFieldRef){
      return;
    }

    const container = this.mathFieldRef.nativeElement as HTMLElement;
    const textInput = container.querySelector('.ML__textarea__textarea') as HTMLElement;
    if (this.ariaLabel && textInput){
      // Legacy MathLive makes this element focusable while also marking it hidden.
      // NVDA and JAWS therefore cannot identify or revisit the math input.
      textInput.removeAttribute('aria-hidden');
      textInput.setAttribute('role', 'textbox');
      textInput.setAttribute('aria-label', this.ariaLabel);
      textInput.setAttribute('aria-multiline', 'false');
      textInput.setAttribute('lang', this.getLanguage());
      textInput.removeAttribute('aria-labelledby');
      // The formula is announced by the live region below. Describing the
      // textbox with that same region makes NVDA/JAWS read it a second time.
      textInput.removeAttribute('aria-describedby');
      this.observeMathInputLabel(textInput);
    }

    this.suppressDuplicateMathLiveFormula(container);

    this.updateKeyboardAccessibility();
  }

  getLanguage(){
    return this.lang.c();
  }

  updateKeyboardAccessibility() {
    if (!this.mathFieldRef) {
      return;
    }

    const container = this.mathFieldRef.nativeElement as HTMLElement;
    const keyboardToggle = container.querySelector('.ML__virtual-keyboard-toggle') as HTMLElement;
    if (!keyboardToggle) {
      return;
    }

    if (this.keyboardToggle !== keyboardToggle) {
      if (this.keyboardToggle) {
        this.keyboardToggle.removeEventListener('click', this.scheduleKeyboardAccessibilityUpdate);
        this.keyboardToggle.removeEventListener('keyup', this.scheduleKeyboardAccessibilityUpdate);
      }
      this.keyboardToggle = keyboardToggle;
      this.keyboardToggle.addEventListener('click', this.scheduleKeyboardAccessibilityUpdate);
      this.keyboardToggle.addEventListener('keyup', this.scheduleKeyboardAccessibilityUpdate);
    }

    if (keyboardToggle.tagName.toLowerCase() !== 'button') {
      keyboardToggle.setAttribute('role', 'button');
      keyboardToggle.setAttribute('tabindex', '0');
    }

    const keyboard = document.querySelector('.ML__keyboard') as HTMLElement;
    if (keyboard) {
      keyboard.id = 'mathlive-virtual-keyboard';
    }

    const isExpanded = this.mathField?.virtualKeyboardState
      ? this.mathField.virtualKeyboardState === 'visible'
      : !!keyboard?.classList.contains('is-visible');
    const translationSlug = isExpanded ? 'mathlive_keyboard_hide' : 'mathlive_keyboard_show';
    const label = this.lang.tra(translationSlug);

    keyboardToggle.setAttribute('aria-label', label);
    keyboardToggle.setAttribute('aria-expanded', `${isExpanded}`);
    keyboardToggle.setAttribute('aria-controls', 'mathlive-virtual-keyboard');
    keyboardToggle.setAttribute('lang', this.getLanguage());
    keyboardToggle.setAttribute('title', label);
    keyboardToggle.setAttribute('data-ML__tooltip', label);
  }

  private scheduleKeyboardAccessibilityUpdate = () => {
    if (this.keyboardA11yTimer) {
      window.clearTimeout(this.keyboardA11yTimer);
    }
    this.keyboardA11yTimer = window.setTimeout(() => this.updateKeyboardAccessibility());
  }

  private observeMathInputLabel(textInput: HTMLElement) {
    if (this.mathTextInput === textInput || typeof MutationObserver === 'undefined') {
      return;
    }

    if (this.mathInputLabelObserver) {
      this.mathInputLabelObserver.disconnect();
    }
    this.mathTextInput = textInput;
    this.mathInputLabelObserver = new MutationObserver(() => {
      if (textInput.getAttribute('aria-label') !== this.ariaLabel) {
        textInput.setAttribute('aria-label', this.ariaLabel);
      }
    });
    this.mathInputLabelObserver.observe(textInput, {
      attributes: true,
      attributeFilter: ['aria-label']
    });
  }

  private suppressDuplicateMathLiveFormula(container: HTMLElement) {
    if (!this.screenReaderTextId) {
      return;
    }

    const mathLiveScreenReaderContainer = Array.from(container.children)
      .find((child: Element) => (
        child.classList.contains('sr-only')
        || child.classList.contains('ML__sr-only')
      ));
    const mathLiveFormula = mathLiveScreenReaderContainer?.children[1] as HTMLElement;
    if (mathLiveFormula) {
      mathLiveFormula.setAttribute('aria-hidden', 'true');
    }
  }

  isReadOnly() {
    return this.editingDisabled.isReadOnly();
  }

}
